import { useMemo } from 'react'

import { Button, MonitoringIssuesChart, SystemStatusesCarousel } from '@/components'
import { useAnalytics } from '@/hooks'
import { SystemStatusCategory } from '@/models'
import { MonitoringSummaryDataType, FormChangeEventHandler, FormChangeEvent } from '@/types'

type MonitoringDonutReportProps = {
    summary: MonitoringSummaryDataType
    isExpanded: boolean

    onChange: FormChangeEventHandler
}

const MonitoringDonutReport = ({
    isExpanded,
    summary,
    ...props
}: MonitoringDonutReportProps) => {
    const { trackEvent } = useAnalytics()
    const connectedSubcategories = useMemo(() => SystemStatusCategory.connectedSitesCategory.subcategories, [])

    const handleChange = (e: FormChangeEvent) => {
        trackEvent('button_click', 'monitoring', `Site Status Alert Prioritization Chart Button ${e.target.value?.toString()}`)
        props.onChange(e)
    }

    return isExpanded
        ? <div
            className="flex flex-wrap gap-8 w-full justify-center"
            onMouseEnter={() => {
                trackEvent('element_hover', 'monitoring', 'Site Status Alert Prioritization Chart')
            }}
        >
            <MonitoringIssuesChart
                secondaryDesign
                key="full-chart"
                summary={summary}
                className="w-35 !h-35"
                onChange={handleChange}
            />
            <div className="shrink-0">
                {connectedSubcategories.map(item =>
                    <div key={item.value}>
                        <h5 className="leading-5 flex items-center gap-2">
                            <Button
                                design="btn-link"
                                className="flex gap-2 items-center"
                                data-test={`monitoring-index-issues-${item.value}-title`}
                                onClick={() => handleChange({
                                    target: {
                                        name: 'statuses',
                                        value: [item.value]
                                    }
                                })}
                            >
                                {item.circleIcon} {item.title}
                            </Button>
                            {item.statuses.length > 1 &&
                                <SystemStatusesCarousel
                                    statuses={item.secondarySortedStatuses}
                                    summary={summary}
                                    data-test={`monitoring-index-issues-${item.value}-carousel`}
                                    onChange={handleChange}
                                />}
                        </h5>
                        <p className="text-xs text-gray-500 leading-[18px]" data-test={`monitoring-index-issues-${item.value}-subtitle`}>
                            {item.subtitle}
                        </p>
                    </div>)}
            </div>
        </div>
        : <div
            className="flex gap-8 w-full justify-center 2xl:-my-9"
            onMouseEnter={() => {
                trackEvent('element_hover', 'monitoring', 'Site Status Alert Prioritization Chart')
            }}
        >
            <MonitoringIssuesChart
                key="half-chart"
                secondaryDesign
                summary={summary}
                className="w-53.5 !h-27"
                type="half-donut"
                onChange={handleChange}
            />
        </div>
}

export default MonitoringDonutReport
